import { useEffect, useState, useRef } from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";

interface Quote {
  id: number;
  content: string;
  bywho: string;
  createDate: string;
}

// Predefined color palette for quotes
const QUOTE_COLORS = [
  "#3498db", // Blue
  "#2ecc71", // Green
  "#e74c3c", // Red
  "#9b59b6", // Purple
  "#f39c12", // Orange
  "#1abc9c", // Teal
  "#d35400", // Dark Orange
  "#2c3e50", // Dark Blue
  "#27ae60", // Emerald
  "#c0392b", // Dark Red
];

// Fallback quotes in case the API is unavailable
const FALLBACK_QUOTES = [
  {
    id: 1,
    content: "The best way to predict the future is to invent it.",
    bywho: "Alan Kay",
    createDate: "2023-01-01"
  },
  {
    id: 2,
    content: "The journey of a thousand miles begins with a single step.",
    bywho: "Lao Tzu",
    createDate: "2023-01-02"
  },
  {
    id: 3, 
    content: "Simplicity is the ultimate sophistication.",
    bywho: "Leonardo da Vinci",
    createDate: "2023-01-03"
  },
  {
    id: 4,
    content: "Life is what happens when you're busy making other plans.",
    bywho: "John Lennon",
    createDate: "2023-01-04"
  },
  {
    id: 5,
    content: "The only way to do great work is to love what you do.",
    bywho: "Steve Jobs",
    createDate: "2023-01-05"
  }
];

export function QuoteDisplay() {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useLocalStorage<number>("lastQuoteFetchTime", 0);
  const [quoteColor, setQuoteColor] = useState(QUOTE_COLORS[0]);
  const timerRef = useRef<number | null>(null);
  const fetchAttemptedRef = useRef(false);

  // Function to get a new random color different from current one
  const getNewRandomColor = () => {
    const currentIndex = QUOTE_COLORS.indexOf(quoteColor);
    let newIndex = currentIndex;
    
    // Make sure we get a different color
    while (newIndex === currentIndex) {
      newIndex = Math.floor(Math.random() * QUOTE_COLORS.length);
    }
    
    return QUOTE_COLORS[newIndex];
  };

  // One-time effect for quote fetching logic
  useEffect(() => {
    // Only run this effect once on mount
    if (fetchAttemptedRef.current) return;
    fetchAttemptedRef.current = true;
    
    const now = Date.now();
    const oneHourInMs = 60 * 60 * 1000; // 1 hour
    
    // Function to get a random fallback quote
    const getRandomFallbackQuote = () => {
      const randomIndex = Math.floor(Math.random() * FALLBACK_QUOTES.length);
      return FALLBACK_QUOTES[randomIndex];
    };
    
    const fetchNewQuote = async () => {
      setLoading(true);
      
      // Set a new color when fetching a new quote
      setQuoteColor(getNewRandomColor());
      
      try {
        // Get the base URL from environment variable or use the backend domain directly
        // Since frontend (nix.pub) and backend (xpp.me) are on different domains
        const apiUrl = import.meta.env.API_URL || 
                      "https://xpp.me"; // Hardcoding the backend domain as fallback
        const response = await fetch(`${apiUrl}/quote`, {
          method: 'GET',
          mode: 'cors',
          credentials: 'include', // Include credentials for proper CORS
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json() as Quote;
          setQuote(data);
        } else {
          console.warn("API request failed, using fallback quote");
          setQuote(getRandomFallbackQuote());
        }
      } catch (error) {
        console.warn("Error fetching quote, using fallback");
        setQuote(getRandomFallbackQuote());
      } finally {
        setLoading(false);
        setLastFetchTime(Date.now());
      }
    };
    
    // Check if we need to fetch a new quote
    if (now - lastFetchTime > oneHourInMs || !quote) {
      fetchNewQuote();
    } else {
      // If we don't need a new quote, just use the one from localStorage or a fallback
      setQuote(getRandomFallbackQuote());
      setLoading(false);
    }
    
    // Set up the 1-hour timer
    timerRef.current = window.setInterval(() => {
      fetchNewQuote();
    }, oneHourInMs);
    
    // Cleanup the timer on unmount
    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
      }
    };
  // Empty dependency array - only run once on mount
  }, []);

  if (loading || !quote) {
    return null;
  }

  // Horizontal display with 30px margin on top and bottom
  return (
    <div className="w-full text-center py-[30px] bg-w-secondary z-30 select-none flex flex-row justify-center items-center">
      <div 
        className="flex flex-row flex-wrap justify-center"
        style={{ 
          color: quoteColor,
          transition: "color 1s ease-in-out"
        }}
      >
        {quote.content.split(" ").map((word, index) => (
          <span 
            key={index} 
            className="mx-1 opacity-80 hover:opacity-100 transition-opacity"
            style={{ 
              animationDelay: `${index * 0.1}s`,
              animationName: "fadeIn",
              animationDuration: "1s",
              animationFillMode: "both" 
            }}
          >
            {word}
          </span>
        ))}
        <span className="ml-2 text-xs opacity-60 self-center flex items-center" style={{ 
          animationDelay: `${quote.content.split(" ").length * 0.1 + 0.2}s`,
          animationName: "fadeIn",
          animationDuration: "1s",
          animationFillMode: "both" 
        }}>
          — {quote.bywho}
        </span>
      </div>
    </div>
  );
}